// DashboardAnalyst.js - Dashboard Variation D: "Analyst Workbench"
// Dense information display for power users

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    Cell
} from 'recharts';
import {
    getCommandCenterSummary,
    formatRelativeTime,
    getSeverityColor
} from '../../services/commandCenterService';
import { useThemeColors } from '../../hooks/useThemeColors';
import { SkeletonDashboard } from '../../components/common';
import './DashboardAnalyst.css';

// Filter bar component
function FilterBar({ filters, onChange }) {
    return (
        <div className="aw-filter-bar">
            <div className="filter-group">
                <label>Asset Type</label>
                <select
                    value={filters.assetType}
                    onChange={(e) => onChange({ ...filters, assetType: e.target.value })}
                >
                    <option value="all">All Types</option>
                    <option value="domain">Domains</option>
                    <option value="ip">IPs</option>
                    <option value="cidr">CIDRs</option>
                </select>
            </div>
            <div className="filter-group">
                <label>Risk Level</label>
                <select
                    value={filters.riskLevel}
                    onChange={(e) => onChange({ ...filters, riskLevel: e.target.value })}
                >
                    <option value="all">All Levels</option>
                    <option value="critical">Critical</option>
                    <option value="atRisk">At Risk</option>
                    <option value="caution">Caution</option>
                    <option value="healthy">Healthy</option>
                </select>
            </div>
            <div className="filter-group">
                <label>Severity</label>
                <select
                    value={filters.severity}
                    onChange={(e) => onChange({ ...filters, severity: e.target.value })}
                >
                    <option value="all">All Severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                </select>
            </div>
            <div className="filter-group search">
                <label>Search</label>
                <input
                    type="text"
                    placeholder="Filter assets..."
                    value={filters.search}
                    onChange={(e) => onChange({ ...filters, search: e.target.value })}
                />
            </div>
        </div>
    );
}

// Quick stats row
function QuickStats({ summary }) {
    return (
        <div className="aw-quick-stats">
            <div className="stat-box">
                <span className="stat-value">{summary.totalAssets || 0}</span>
                <span className="stat-label">Assets</span>
            </div>
            <div className="stat-box critical">
                <span className="stat-value">{summary.totalVulnerabilities?.critical || 0}</span>
                <span className="stat-label">Critical</span>
            </div>
            <div className="stat-box high">
                <span className="stat-value">{summary.totalVulnerabilities?.high || 0}</span>
                <span className="stat-label">High</span>
            </div>
            <div className="stat-box medium">
                <span className="stat-value">{summary.totalVulnerabilities?.medium || 0}</span>
                <span className="stat-label">Medium</span>
            </div>
            <div className="stat-box">
                <span className="stat-value">{summary.overallRiskScore || 0}</span>
                <span className="stat-label">Score</span>
            </div>
        </div>
    );
}

// Asset row with inline actions
function AssetRow({ asset, onAction }) {
    const getRiskClass = (level) => {
        if (level === 'critical') return 'risk-critical';
        if (level === 'atRisk') return 'risk-atrisk';
        if (level === 'caution') return 'risk-caution';
        return 'risk-healthy';
    };

    return (
        <tr className="aw-asset-row">
            <td className="col-status">
                <span className={`status-dot ${getRiskClass(asset.riskLevel)}`} />
            </td>
            <td className="col-asset">
                <span className="asset-name">{asset.value}</span>
                <span className="asset-type">{asset.assetType}</span>
            </td>
            <td className="col-score">
                <span className={`score ${getRiskClass(asset.riskLevel)}`}>
                    {asset.riskScore}
                </span>
            </td>
            <td className="col-findings">
                <span className="finding critical">{asset.vulnCounts?.critical || 0}</span>
                <span className="finding high">{asset.vulnCounts?.high || 0}</span>
                <span className="finding medium">{asset.vulnCounts?.medium || 0}</span>
                <span className="finding low">{asset.vulnCounts?.low || 0}</span>
            </td>
            <td className="col-trend">
                <span className={`trend trend-${asset.trend}`}>
                    {asset.trend === 'improving' ? '↑' : asset.trend === 'worsening' ? '↓' : '→'}
                </span>
            </td>
            <td className="col-updated">{formatRelativeTime(asset.lastScanAt)}</td>
            <td className="col-actions">
                <button onClick={() => onAction('view', asset)} title="View">👁</button>
                <button onClick={() => onAction('scan', asset)} title="Scan">⟳</button>
                <button onClick={() => onAction('vulns', asset)} title="Findings">📋</button>
            </td>
        </tr>
    );
}

// Finding row for the findings panel
function FindingRow({ finding, onClick }) {
    return (
        <div className="aw-finding-row" onClick={onClick}>
            <span
                className="finding-severity"
                style={{ color: getSeverityColor(finding.severity) }}
            >
                {finding.severity?.charAt(0).toUpperCase()}
            </span>
            <div className="finding-info">
                <span className="finding-title">{finding.title || finding.name}</span>
                <span className="finding-asset">{finding.assetValue || finding.asset}</span>
            </div>
        </div>
    );
}

function DashboardAnalyst() {
    const navigate = useNavigate();
    const themeColors = useThemeColors();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        summary: {},
        assets: [],
        topRisks: [],
        trends: {}
    });
    const [filters, setFilters] = useState({
        assetType: 'all',
        riskLevel: 'all',
        severity: 'all',
        search: ''
    });
    const [sortConfig, setSortConfig] = useState({ key: 'riskScore', direction: 'asc' });
    const [activePanel, setActivePanel] = useState('findings');

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await getCommandCenterSummary();
                if (result.success) {
                    setData({
                        summary: result.summary || {},
                        assets: result.assets || [],
                        topRisks: result.topRisks || [],
                        trends: result.trends || {}
                    });
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const { summary, assets, topRisks } = data;

    // Filter and sort assets
    const filteredAssets = useMemo(() => {
        let result = [...assets];

        // Apply filters
        if (filters.assetType !== 'all') {
            result = result.filter(a => a.assetType === filters.assetType);
        }
        if (filters.riskLevel !== 'all') {
            result = result.filter(a => a.riskLevel === filters.riskLevel);
        }
        if (filters.search) {
            const search = filters.search.toLowerCase();
            result = result.filter(a => a.value.toLowerCase().includes(search));
        }

        // Apply sort
        result.sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            const direction = sortConfig.direction === 'asc' ? 1 : -1;
            if (typeof aVal === 'number') {
                return (aVal - bVal) * direction;
            }
            return String(aVal).localeCompare(String(bVal)) * direction;
        });

        return result;
    }, [assets, filters, sortConfig]);

    // Chart data for severity distribution — uses theme-aware colors
    const severityChartData = useMemo(() => [
        { name: 'Critical', value: summary.totalVulnerabilities?.critical || 0, color: themeColors.severity.critical },
        { name: 'High', value: summary.totalVulnerabilities?.high || 0, color: themeColors.severity.high },
        { name: 'Medium', value: summary.totalVulnerabilities?.medium || 0, color: themeColors.severity.medium },
        { name: 'Low', value: summary.totalVulnerabilities?.low || 0, color: themeColors.severity.low }
    ], [summary.totalVulnerabilities, themeColors.severity]);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Handle asset action
    const handleAssetAction = (action, asset) => {
        switch (action) {
            case 'view':
                navigate(`/assets/${asset.assetId}`);
                break;
            case 'scan':
                // Trigger scan
                console.log('Trigger scan for', asset.value);
                break;
            case 'vulns':
                navigate(`/assets/${asset.assetId}#vulnerabilities`);
                break;
            default:
                break;
        }
    };

    if (loading) {
        return (
            <div className="aw-page">
                <header className="aw-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Analyst Workbench</h1>
                </header>
                <SkeletonDashboard />
            </div>
        );
    }

    if (error) {
        return (
            <div className="aw-page">
                <header className="aw-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Analyst Workbench</h1>
                </header>
                <div className="error-state">
                    <span className="error-icon">⚠️</span>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="aw-page">
            {/* Header */}
            <header className="aw-header">
                <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                <h1>
                    <span className="header-icon">🔬</span>
                    Analyst Workbench
                </h1>
                <QuickStats summary={summary} />
            </header>

            {/* Filter Bar */}
            <FilterBar filters={filters} onChange={setFilters} />

            {/* Main Grid */}
            <div className="aw-main-grid">
                {/* Asset Table */}
                <div className="aw-table-panel">
                    <div className="panel-header">
                        <h2>Assets ({filteredAssets.length})</h2>
                        <div className="panel-actions">
                            <button onClick={() => navigate('/assets')}>View All</button>
                            <button onClick={() => window.location.reload()}>Refresh</button>
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="aw-table">
                            <thead>
                                <tr>
                                    <th className="col-status"></th>
                                    <th className="col-asset" onClick={() => handleSort('value')}>
                                        Asset {sortConfig.key === 'value' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="col-score" onClick={() => handleSort('riskScore')}>
                                        Score {sortConfig.key === 'riskScore' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="col-findings">C/H/M/L</th>
                                    <th className="col-trend">Trend</th>
                                    <th className="col-updated">Updated</th>
                                    <th className="col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAssets.map(asset => (
                                    <AssetRow
                                        key={asset.assetId}
                                        asset={asset}
                                        onAction={handleAssetAction}
                                    />
                                ))}
                                {filteredAssets.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="empty-row">
                                            No assets match the current filters
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Right Sidebar */}
                <div className="aw-sidebar">
                    {/* Panel Tabs */}
                    <div className="sidebar-tabs">
                        <button
                            className={activePanel === 'findings' ? 'active' : ''}
                            onClick={() => setActivePanel('findings')}
                        >
                            Findings
                        </button>
                        <button
                            className={activePanel === 'chart' ? 'active' : ''}
                            onClick={() => setActivePanel('chart')}
                        >
                            Chart
                        </button>
                    </div>

                    {/* Findings Panel */}
                    {activePanel === 'findings' && (
                        <div className="sidebar-panel">
                            <h3>Top Findings ({topRisks.length})</h3>
                            <div className="findings-list">
                                {topRisks.slice(0, 15).map((finding, i) => (
                                    <FindingRow
                                        key={i}
                                        finding={finding}
                                        onClick={() => navigate(`/assets/${finding.assetId}#vulnerabilities`)}
                                    />
                                ))}
                                {topRisks.length === 0 && (
                                    <div className="empty-findings">No findings to display</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Chart Panel */}
                    {activePanel === 'chart' && (
                        <div className="sidebar-panel">
                            <h3>Severity Distribution</h3>
                            <div className="chart-container">
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={severityChartData} layout="vertical">
                                        <XAxis type="number" hide />
                                        <YAxis
                                            type="category"
                                            dataKey="name"
                                            width={60}
                                            tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                                        />
                                        <RechartsTooltip
                                            contentStyle={{
                                                background: 'var(--bg-secondary)',
                                                border: '1px solid var(--border-medium)',
                                                borderRadius: '4px'
                                            }}
                                        />
                                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                            {severityChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Distribution counts */}
                            <div className="distribution-stats">
                                <div className="dist-stat">
                                    <span className="dist-label">At Risk Assets</span>
                                    <span className="dist-value">
                                        {(summary.riskDistribution?.critical || 0) + (summary.riskDistribution?.atRisk || 0)}
                                    </span>
                                </div>
                                <div className="dist-stat">
                                    <span className="dist-label">Healthy Assets</span>
                                    <span className="dist-value healthy">{summary.riskDistribution?.healthy || 0}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DashboardAnalyst;
